import { fundingHqFetch, getFundingApiErrorMessage } from './fundingApi';
import { buildContactFullName, parseBusinessStartDate, parseReadinessPercent, isMaskedSensitiveValue } from '../../utils/fundingClientIntakeUtils';

export const FUNDING_CLIENT_SOURCES = {
  MANUAL: 'manual_intake',
  UPLOAD: 'uploaded_scan',
  PASTE: 'pasted_client_info',
  BULK: 'bulk_import',
};

const SOURCE_LABELS = {
  manual_intake: 'Manual',
  uploaded_scan: 'Uploaded Scan',
  pasted_client_info: 'Pasted Info',
  bulk_import: 'Bulk Import',
  sample: 'Sample',
};

function buildClientsQuery({ agencyId, search, status } = {}) {
  const params = new URLSearchParams();
  if (agencyId) params.set('agency_id', agencyId);
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  const query = params.toString();
  return query ? `?${query}` : '';
}

function parseMoney(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function splitOwnerName(ownerName = '') {
  const parts = String(ownerName).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { firstName: '', middleName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], middleName: '', lastName: '' };
  if (parts.length === 2) return { firstName: parts[0], middleName: '', lastName: parts[1] };
  return { firstName: parts[0], middleName: parts.slice(1, -1).join(' '), lastName: parts[parts.length - 1] };
}

function normalizeCreditScore(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(String(value).replace(/\D/g, ''));
  if (!Number.isFinite(parsed) || parsed < 300 || parsed > 850) return null;
  return parsed;
}

export function formatFundingClientSource(source) {
  return SOURCE_LABELS[source] || (source ? String(source) : 'Manual');
}

/** Resolve persisted client ID used for API scoping (applications, documents, etc.). */
export function resolveSelectedClientId(client) {
  if (!client) return null;
  const persistedId = client.id || client.clientId || client.client_id;
  if (persistedId == null || persistedId === '') return null;
  return String(persistedId);
}

/** True for saved backend clients that are not sample/demo workspaces. */
export function isRealPersistedClient(client) {
  return Boolean(resolveSelectedClientId(client)) && !client?.isSample;
}

export function buildClientIntakeFromApi(apiClient = {}) {
  if (!apiClient) return {};
  return {
    firstName: apiClient.firstName || '',
    middleName: apiClient.middleName || '',
    lastName: apiClient.lastName || '',
    email: apiClient.email || '',
    phone: apiClient.phone || '',
    status: apiClient.status || '',
    source: apiClient.source || '',
    ownership: apiClient.ownershipPercentage ?? '',
    ssnLast4: apiClient.ssnLast4 || '',
    ssnMasked: apiClient.ssnMasked || '',
    hasClientSsn: Boolean(apiClient.hasClientSsn ?? apiClient.ssnLast4),
    dob: apiClient.dob || '',
    personalAddress: apiClient.personalAddress || '',
    timeLiveInHouse: apiClient.timeLiveInHouse || '',
    housingPayment: apiClient.housingPayment || '',
    businessLegalName: apiClient.businessName || apiClient.legalBusinessName || '',
    einMasked: apiClient.einMasked || '',
    hasClientEin: Boolean(apiClient.hasClientEin ?? apiClient.einLast4),
    businessStartDate: apiClient.businessStartDate || '',
    businessAddress: apiClient.businessAddress || '',
    businessPhone: apiClient.businessPhone || '',
    businessEmail: apiClient.businessEmail || '',
    industry: apiClient.industry || '',
    entityType: apiClient.entityType || '',
    personalIncome: apiClient.personalIncome ?? '',
    businessIncome: apiClient.businessIncome ?? '',
    employer: apiClient.employer || '',
    timeWorkingAt: apiClient.timeWorkingAt || '',
    monthlyRevenue: apiClient.monthlyRevenue ?? '',
    monthlyDeposits: apiClient.monthlyDeposits ?? '',
    businessExpenses: apiClient.businessExpenses ?? '',
    savings: apiClient.savings ?? '',
    retirement: apiClient.retirementAccount ?? '',
    bankName: apiClient.bankName || '',
    experian: apiClient.experianScore ?? apiClient.creditScore ?? '',
    transUnion: apiClient.transunionScore ?? '',
    equifax: apiClient.equifaxScore ?? '',
    highestLimit: apiClient.currentHighestLimit ?? '',
    inquiries: apiClient.openInquiries ?? '',
    utilization: apiClient.utilizationPercent || '',
    derogatoryItems: apiClient.derogatoryItems ?? '',
    readiness: apiClient.readinessPercent ?? apiClient.readinessScore ?? '',
    desiredFunding: apiClient.requestedAmount ?? '',
    targetProduct: apiClient.targetProduct || '',
    fundingStage: apiClient.fundingStage || '',
    applicationType: apiClient.applicationType || '',
    personalFundingAmount: apiClient.personalFundingAmount ?? '',
    zeroPercentAmount: apiClient.zeroPercentCreditCardAmount ?? '',
    businessLocAmount: apiClient.businessLocAmount ?? '',
    targetLender: apiClient.targetLender || '',
    applicationEmailCreated: apiClient.applicationEmailCreated || '',
    tags: apiClient.tags || '',
    advisorOwner: apiClient.advisorOwner || '',
    notes: apiClient.notes || '',
  };
}

export function normalizeFundingClient(apiClient) {
  if (!apiClient) return null;
  const persistedId = apiClient.id || apiClient.client_id || apiClient.clientId || null;
  const displayId = apiClient.display_id
    || apiClient.displayId
    || apiClient.client_number
    || apiClient.clientNumber
    || apiClient.clientCode
    || (apiClient.clientId && apiClient.clientId !== persistedId ? apiClient.clientId : null)
    || (apiClient.client_id && apiClient.client_id !== persistedId ? apiClient.client_id : null)
    || persistedId;
  const isSample = Boolean(apiClient.isSample ?? apiClient.is_sample);
  const intake = {
    ...buildClientIntakeFromApi(apiClient),
    ...(apiClient.intake || {}),
  };
  return {
    ...apiClient,
    id: persistedId,
    clientId: persistedId,
    displayId,
    clientCode: apiClient.clientCode || displayId,
    isSample,
    clientType: isSample ? 'Sample' : 'Live',
    sourceLabel: formatFundingClientSource(apiClient.source),
    intake,
  };
}

export function isFundingClientPersistenceDebugEnabled() {
  return process.env.NODE_ENV !== 'production'
    && process.env.REACT_APP_FUNDING_CLIENT_DEBUG === 'true';
}

function logPersistenceDebug(message, meta = {}) {
  if (!isFundingClientPersistenceDebugEnabled()) return;
  const safe = { ...meta };
  ['ssn', 'ein', 'ssnFull', 'einFull', 'ssnMasked', 'einMasked'].forEach((key) => {
    delete safe[key];
  });
  console.info(`[FundingClients] ${message}`, safe);
}

function parseOptionalInteger(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(String(value).replace(/\D/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function parseOptionalPercent(value) {
  if (value === '' || value == null) return null;
  const parsed = Number(String(value).replace(/[^\d.]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function buildManualIntakeApiFields(form = {}) {
  const firstName = (form.firstName || '').trim();
  const middleName = (form.middleName || '').trim();
  const lastName = (form.lastName || '').trim();
  const notes = [form.notes?.trim(), form.tags ? `Tags: ${form.tags}` : '']
    .filter(Boolean)
    .join('\n');

  const payload = {
    first_name: firstName || null,
    middle_name: middleName || null,
    last_name: lastName || null,
    email: form.email?.trim() || null,
    phone: form.phone?.trim() || null,
    business_name: form.businessLegalName?.trim() || null,
    legal_business_name: form.businessLegalName?.trim() || null,
    industry: form.industry?.trim() || null,
    entity_type: form.entityType?.trim() || null,
    state: form.personalState?.trim() || form.businessState?.trim() || form.state?.trim() || null,
    business_start_date: parseBusinessStartDate(form.businessStartDate),
    monthly_revenue: parseMoney(form.monthlyRevenue),
    annual_revenue: parseMoney(form.personalIncome) || parseMoney(form.businessIncome),
    requested_amount: parseMoney(form.desiredFunding),
    credit_score: normalizeCreditScore(form.experian || form.creditScore),
    status: form.status?.trim() || 'Draft',
    notes: notes || null,
    source: form.source?.trim() || null,
    application_email_created: form.applicationEmailCreated?.trim() || null,
    ownership_percentage: parseOptionalPercent(form.ownership),
    dob: form.dob?.trim() || null,
    personal_address: form.personalAddress?.trim() || null,
    time_live_in_house: form.timeLiveInHouse?.trim() || null,
    housing_payment: form.housingPayment?.trim() || null,
    business_address: form.businessAddress?.trim() || null,
    business_phone: form.businessPhone?.trim() || null,
    business_email: form.businessEmail?.trim() || null,
    personal_income: parseMoney(form.personalIncome),
    business_income: parseMoney(form.businessIncome),
    employer: form.employer?.trim() || null,
    time_working_at: form.timeWorkingAt?.trim() || null,
    monthly_deposits: parseMoney(form.monthlyDeposits),
    business_expenses: parseMoney(form.businessExpenses),
    savings: parseMoney(form.savings),
    retirement_account: form.retirement?.trim() || null,
    bank_name: form.bankName?.trim() || null,
    experian_score: normalizeCreditScore(form.experian),
    transunion_score: normalizeCreditScore(form.transUnion),
    equifax_score: normalizeCreditScore(form.equifax),
    current_highest_limit: parseMoney(form.highestLimit),
    open_inquiries: parseOptionalInteger(form.inquiries),
    utilization_percent: form.utilization?.trim() || null,
    derogatory_items: parseOptionalInteger(form.derogatoryItems),
    readiness_percent: parseReadinessPercent(form.readiness),
    target_product: form.targetProduct?.trim() || null,
    funding_stage: form.fundingStage?.trim() || null,
    application_type: form.applicationType?.trim() || null,
    personal_funding_amount: parseMoney(form.personalFundingAmount),
    zero_percent_credit_card_amount: parseMoney(form.zeroPercentAmount),
    business_loc_amount: parseMoney(form.businessLocAmount),
    target_lender: form.targetLender?.trim() || null,
    tags: form.tags?.trim() || null,
    advisor_owner: form.advisorOwner?.trim() || null,
  };

  const ssn = form.ssn?.trim();
  if (ssn && !isMaskedSensitiveValue(ssn)) {
    payload.ssn = ssn;
  }

  const ein = form.ein?.trim();
  if (ein && !isMaskedSensitiveValue(ein)) {
    payload.ein = ein;
  }

  return payload;
}

export function validateManualIntakeForm() {
  return null;
}

export function manualIntakeFormToCreatePayload(form, { agencyId, source = FUNDING_CLIENT_SOURCES.MANUAL } = {}) {
  if (!agencyId) {
    throw new Error('Agency is required to create a funding client.');
  }

  const payload = {
    agency_id: agencyId,
    ...buildManualIntakeApiFields(form),
    source: source || form.source?.trim() || FUNDING_CLIENT_SOURCES.MANUAL,
  };

  const applicationPassword = form.applicationPasswordCreated?.trim();
  if (applicationPassword && !isMaskedSensitiveValue(applicationPassword)) {
    payload.application_password_created = applicationPassword;
  }

  return payload;
}

export function manualIntakeFormToUpdatePayload(form) {
  const payload = buildManualIntakeApiFields(form);
  const applicationPassword = form.applicationPasswordCreated?.trim();
  if (applicationPassword && !isMaskedSensitiveValue(applicationPassword)) {
    payload.application_password_created = applicationPassword;
  }
  return payload;
}

export function deriveManualIntakeOwnerName(form = {}) {
  return buildContactFullName(form) || 'New Client';
}

export function extractedClientDataToCreatePayload(clientData = {}, { agencyId, source, notes } = {}) {
  const hasContactNames = Boolean(clientData.firstName?.trim() || clientData.lastName?.trim() || clientData.middleName?.trim());
  const ownerSplit = hasContactNames
    ? {
      firstName: clientData.firstName,
      middleName: clientData.middleName,
      lastName: clientData.lastName,
    }
    : splitOwnerName(clientData.ownerName || '');

  return manualIntakeFormToCreatePayload({
    firstName: ownerSplit.firstName,
    middleName: ownerSplit.middleName,
    lastName: ownerSplit.lastName,
    email: clientData.email,
    phone: clientData.phone,
    businessLegalName: clientData.businessLegalName || clientData.businessName,
    industry: clientData.industry,
    entityType: clientData.entityType,
    personalState: clientData.personalState,
    businessState: clientData.businessState,
    businessStartDate: clientData.businessStartDate,
    monthlyRevenue: clientData.monthlyRevenue,
    personalIncome: clientData.personalIncome,
    businessIncome: clientData.businessIncome,
    desiredFunding: clientData.desiredFunding || clientData.requestedAmount,
    experian: clientData.experian || clientData.creditScore,
    status: 'Documents Pending',
    notes,
  }, { agencyId, source });
}

export function resolveIntakeSource(payload = {}) {
  const source = String(payload.source || '').toLowerCase();
  if (payload.pastedText || source.includes('pasted')) {
    return FUNDING_CLIENT_SOURCES.PASTE;
  }
  if (payload.documents?.length || source.includes('document') || source.includes('upload')) {
    return FUNDING_CLIENT_SOURCES.UPLOAD;
  }
  return FUNDING_CLIENT_SOURCES.MANUAL;
}

export async function fetchFundingClients({ agencyId, search, status } = {}) {
  logPersistenceDebug('list request', { agencyId, search, status });
  const data = await fundingHqFetch(`/clients${buildClientsQuery({ agencyId, search, status })}`);
  const clients = (data.clients ?? []).map(normalizeFundingClient);
  logPersistenceDebug('list response', { agencyId, count: clients.length });
  return {
    clients,
    total: data.total ?? clients.length,
  };
}

export async function fetchFundingClient(clientId, agencyId) {
  const params = new URLSearchParams({ agency_id: agencyId });
  const data = await fundingHqFetch(`/clients/${encodeURIComponent(clientId)}?${params}`);
  return normalizeFundingClient(data);
}

export async function createFundingClient(payload) {
  logPersistenceDebug('create request', {
    agency_id: payload.agency_id,
    source: payload.source,
  });
  try {
    const data = await fundingHqFetch('/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const normalized = normalizeFundingClient(data);
    logPersistenceDebug('create response', {
      id: normalized?.id,
      agencyId: normalized?.agencyId,
      source: normalized?.source,
    });
    return normalized;
  } catch (error) {
    const wrapped = new Error(
      getFundingApiErrorMessage(error, 'Unable to create client. Please review required fields.'),
    );
    wrapped.status = error?.status;
    wrapped.data = error?.data;
    throw wrapped;
  }
}

export async function updateFundingClient(clientId, agencyId, payload) {
  const params = new URLSearchParams({ agency_id: agencyId });
  try {
    const data = await fundingHqFetch(`/clients/${encodeURIComponent(clientId)}?${params}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return normalizeFundingClient(data);
  } catch (error) {
    const wrapped = new Error(getFundingApiErrorMessage(error, 'Unable to save client changes.'));
    wrapped.status = error?.status;
    wrapped.data = error?.data;
    throw wrapped;
  }
}

export async function deleteFundingClient(clientId, agencyId) {
  const params = new URLSearchParams({ agency_id: agencyId });
  return fundingHqFetch(`/clients/${encodeURIComponent(clientId)}?${params}`, {
    method: 'DELETE',
  });
}

function extractRevealPlaintext(response, keys = []) {
  if (!response) return '';
  if (typeof response === 'string') return response.trim();
  for (const key of keys) {
    const value = response?.[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  if (response?.data && typeof response.data === 'object') {
    for (const key of keys) {
      const nested = response.data[key];
      if (nested != null && String(nested).trim() !== '') {
        return String(nested).trim();
      }
    }
    if (response.data.value != null && String(response.data.value).trim() !== '') {
      return String(response.data.value).trim();
    }
  }
  if (response?.value != null && String(response.value).trim() !== '') {
    return String(response.value).trim();
  }
  return '';
}

async function postClientReveal(clientId, agencyId, revealPath, keys, fallbackMessage) {
  if (!clientId) {
    throw new Error(fallbackMessage);
  }
  if (!agencyId) {
    throw new Error(fallbackMessage);
  }
  const params = new URLSearchParams({ agency_id: agencyId });
  try {
    const data = await fundingHqFetch(
      `/clients/${encodeURIComponent(clientId)}/${revealPath}?${params}`,
      { method: 'POST' },
    );
    const value = extractRevealPlaintext(data, keys);
    if (!value) {
      throw new Error(fallbackMessage);
    }
    return value;
  } catch (error) {
    const wrapped = new Error(getFundingApiErrorMessage(error, fallbackMessage));
    wrapped.status = error?.status;
    wrapped.code = error?.code || error?.data?.code;
    wrapped.field = error?.field || error?.data?.field;
    wrapped.data = error?.data;
    throw wrapped;
  }
}

export async function revealClientSsn(clientId, agencyId) {
  return postClientReveal(clientId, agencyId, 'reveal-ssn', ['ssn', 'full_ssn', 'value'], 'Unable to reveal SSN');
}

export async function revealClientEin(clientId, agencyId) {
  return postClientReveal(clientId, agencyId, 'reveal-ein', ['ein', 'full_ein', 'value'], 'Unable to reveal EIN');
}

export async function revealClientApplicationPassword(clientId, agencyId) {
  return postClientReveal(
    clientId,
    agencyId,
    'reveal-application-password',
    ['application_password_created', 'password', 'value'],
    'Unable to reveal password',
  );
}

export async function revealApplicationPassword(clientId, agencyId) {
  return revealClientApplicationPassword(clientId, agencyId);
}

export function emptyFundingClientForm(agencyId = '') {
  return {
    agency_id: agencyId,
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    business_name: '',
    legal_business_name: '',
    industry: '',
    entity_type: '',
    state: '',
    business_start_date: '',
    monthly_revenue: '',
    annual_revenue: '',
    requested_amount: '',
    use_of_funds: '',
    credit_score: '',
    status: 'Draft',
    notes: '',
  };
}

export function clientToFormValues(client) {
  if (!client) return emptyFundingClientForm();
  return {
    agency_id: client.agencyId || '',
    first_name: client.firstName || '',
    last_name: client.lastName || '',
    email: client.email || '',
    phone: client.phone || '',
    business_name: client.businessName || '',
    legal_business_name: client.legalBusinessName || '',
    industry: client.industry || '',
    entity_type: client.entityType || '',
    state: client.state || '',
    business_start_date: client.businessStartDate || '',
    monthly_revenue: client.monthlyRevenue ?? '',
    annual_revenue: client.annualRevenue ?? '',
    requested_amount: client.requestedAmount ?? '',
    use_of_funds: client.useOfFunds || '',
    credit_score: client.creditScore ?? '',
    status: client.status || 'Draft',
    notes: client.notes || '',
  };
}

export function formValuesToPayload(values) {
  const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    agency_id: values.agency_id,
    first_name: values.first_name?.trim(),
    last_name: values.last_name?.trim(),
    email: values.email?.trim() || null,
    phone: values.phone?.trim() || null,
    business_name: values.business_name?.trim() || null,
    legal_business_name: values.legal_business_name?.trim() || null,
    industry: values.industry?.trim() || null,
    entity_type: values.entity_type || null,
    state: values.state?.trim() || null,
    business_start_date: values.business_start_date || null,
    monthly_revenue: toNumber(values.monthly_revenue),
    annual_revenue: toNumber(values.annual_revenue),
    requested_amount: toNumber(values.requested_amount),
    use_of_funds: values.use_of_funds?.trim() || null,
    credit_score: values.credit_score === '' ? null : Number(values.credit_score),
    status: values.status || 'Draft',
    notes: values.notes?.trim() || null,
  };
}
